import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { downloadBlob } from "@/lib/workflow";
import { AlertTriangle, CheckCircle2, Loader2, Printer, XCircle } from "lucide-react";

const fmt = (v) => String(Math.round(v * 10) / 10).replace(".", ",");

export function PrintDialog({ open, onClose, space, today }) {
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { setCheck(null); return; }
    api.get(`/banner/${space.id}/print-check`).then((r) => setCheck(r.data)).catch((e) => setCheck({ error: apiError(e.response?.data?.detail) }));
  }, [open, space.id]);
  const errors = check?.issues?.filter((i) => i.level === "error") || [];
  const warns = check?.issues?.filter((i) => i.level === "warn") || [];
  const create = async () => {
    setBusy(true);
    try { await downloadBlob("/banner/export/print", { space_id: space.id }, `Druck Werbebanner ${space.name} 1-${check?.scale || 10} ${today}.pdf`); onClose(); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="banner-print-dialog">
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Printer size={18} /> Druck-PDF · {space.name}</DialogTitle></DialogHeader>
        {!check && <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-slate-400" /></div>}
        {check?.error && <p className="text-sm text-red-600">{check.error}</p>}
        {check?.issues && <div className="space-y-3 text-sm">
          {check.final_mm && <p className="text-slate-600" data-testid="banner-print-format">Endformat {fmt(check.final_mm[0] / 10)} × {fmt(check.final_mm[1] / 10)} cm · Datei 1:{check.scale || 10} {fmt(check.file_mm[0])} × {fmt(check.file_mm[1])} mm inkl. 5 mm Beschnitt (Endformat) · PDF/X-4 · Fogra39 · Grün 92/43/69/37 · Logos Silbergrau 10/0/0/40</p>}
          <p className="text-slate-800">{check.filled} von {check.slots} Logofeldern werden gedruckt, freie Felder und Sperrbereiche bleiben grün.</p>
          {!check.issues.length && <p className="flex items-center gap-2 text-emerald-700" data-testid="banner-print-ok"><CheckCircle2 size={16} /> Alle Logos sind druckfertig.</p>}
          <ul className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="banner-print-issues">
            {[...errors, ...warns].map((i, k) => (
              <li key={k} className={`flex gap-2 ${i.level === "error" ? "text-red-700" : "text-amber-800"}`}>
                {i.level === "error" ? <XCircle size={15} className="mt-0.5 shrink-0" /> : <AlertTriangle size={15} className="mt-0.5 shrink-0" />}
                <span>{i.num ? <b>Feld {i.num} · {i.name}: </b> : null}{i.msg}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500">Bitte die Datei vor dem Druck von der Druckerei pr&uuml;fen lassen (Preflight).</p>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button disabled={busy || !check.final_mm} onClick={create} className="bg-[#005448] hover:bg-[#00433a]" data-testid="banner-print-create">
              {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Printer size={14} className="mr-1.5" />}{errors.length || warns.length ? "Trotzdem erstellen" : "Druck-PDF erstellen"}
            </Button>
          </div>
        </div>}
      </DialogContent>
    </Dialog>
  );
}
